import Header from '../components/Header';
import Footer from '../components/Footer';
import { SchemaInjector } from '../components/SchemaInjector';
import { CheckCircle, Shield, Scissors, Camera } from 'lucide-react';

const RootRemovalPricing = () => {
  // Unified Schema for the entire page
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/root-removal-pricing#webpage",
        "url": "https://colorado-plumbing.com/root-removal-pricing",
        "name": "Denver Sewer Root Removal Cost: $495 Flat-Rate | Colorado First Plumbing",
        "description": "See exact, flat-rate pricing for sewer root removal in Denver. Standard root cutting starts at $495. Premium service with video proof available. 360 Google Reviews.",
        "isPartOf": { "@id": "https://colorado-plumbing.com/#website" },
        "breadcrumb": { "@id": "https://colorado-plumbing.com/root-removal-pricing#breadcrumb" }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/root-removal-pricing#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://colorado-plumbing.com/" },
          { "@type": "ListItem", "position": 2, "name": "Sewer Root Removal Pricing", "item": "https://colorado-plumbing.com/root-removal-pricing" }
        ]
      },
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave", "addressLocality": "Littleton", "addressRegion": "CO", "postalCode": "80130", "addressCountry": "US"
        },
        "url": "https://colorado-plumbing.com/root-removal-pricing",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "areaServed": [
          { "@type": "City", "name": "Denver" },
          { "@type": "AdministrativeArea", "name": "Denver Metro" }
        ]
      },
      {
        "@type": "Product",
        "name": "Standard Root Removal via Cleanout",
        "description": "Our $495 standard service includes professional root cutting in the main sewer line via an accessible cleanout. We use specialized Milwaukee equipment and provide digital photos of our findings.",
        "sku": "ROOT-STD-CLEANOUT-495",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/root-removal-pricing#root-removal-pricing-details", "priceCurrency": "USD", "price": "495", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Standard Root Removal via Pulled Toilet",
        "description": "For homes without an accessible cleanout, our $695 standard service includes pulling a toilet to access the mainline for professional root cutting. The price includes resetting the toilet with a new wax ring and bolts.",
        "sku": "ROOT-STD-TOILET-695",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/root-removal-pricing#root-removal-pricing-details", "priceCurrency": "USD", "price": "695", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product",
        "name": "Premium Root Removal with Full Video Recording",
        "description": "Our premium $795 service includes everything from the standard root cutting, plus a full before-and-after sewer video camera inspection. A copy of the video is provided to you for your records.",
        "sku": "ROOT-PREMIUM-VIDEO-795",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/root-removal-pricing#root-removal-pricing-details", "priceCurrency": "USD", "price": "795", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much does sewer root removal cost in Denver?",
            "acceptedAnswer": { "@type": "Answer", "text": "Colorado First Plumbing offers flat-rate pricing for root removal in the Denver area. Our standard service with photo proof is $495 via a cleanout or $695 via a pulled toilet. For an additional fee, our premium service at $795 includes a full video recording of the cleaning." }
          },
          {
            "@type": "Question",
            "name": "What is the difference between standard and premium root removal?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our standard service uses professional root-cutting equipment and provides digital photos of any notable findings. The premium service includes everything in the standard package, plus a full before-and-after video camera inspection of the sewer line, which is provided to you for your records." }
          },
          {
            "@type": "Question",
            "name": "Is root removal a permanent solution for tree roots in pipes?",
            "acceptedAnswer": { "@type": "Answer", "text": "Root removal is a highly effective maintenance solution that clears the pipe and restores flow. However, if the pipe is damaged, roots will eventually grow back. Regular maintenance (every 1-2 years) is the most affordable way to manage them. For a permanent solution, a spot repair or full sewer line replacement may be necessary." }
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-neutral-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-48 pb-16 bg-gradient-to-br from-action-urgent to-authority-navy">
        <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
          <div className="inline-block px-6 py-3 mb-8 rounded-full bg-success-primary/90 text-white font-bold text-lg">
            ✂️ Professional Root Cutting • Video Documentation • Specialized Equipment
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
            Sewer Root Removal Cost in Denver
            <span className="block text-2xl md:text-3xl text-success-light font-semibold mt-2">
              Exact Prices for Tree Root Cutting Services
            </span>
          </h1>
          
          <p className="text-lg text-trust-light/90 mb-8">
            Master Plumber Owned • 360 Google Reviews • Specialized Root Cutting Equipment with Documentation
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            
            <a 
              href="#root-removal-pricing-details" 
              className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
            >
              💰 See All Pricing Below
            </a>
          </div>
        </div>
      </section>
      {/* Pricing Section */}
      <section id="root-removal-pricing-details" className="py-20 bg-neutral-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-authority-navy">
              Transparent Root Removal Pricing
            </h2>
            <p className="text-xl text-neutral-gray mb-4">
              See actual sewer root cutting costs with no hidden fees or surprise charges
            </p>
            <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
              <Shield className="w-6 h-6 text-success-primary mr-3" />
              <span className="text-success-primary font-bold">3 Root Removal Options • All Pricing Online</span>
            </div>
          </div>

          {/* Standard Root Removal */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-trust-light to-success-light/30 rounded-2xl p-8 border-2 border-trust-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Scissors size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Standard Root Removal with Photos</h3>
                  <p className="text-lg text-neutral-gray">Professional root cutting service with digital photo documentation</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-trust-primary to-trust-deep text-white p-4">
                      <h4 className="text-xl font-bold text-center">Root Removal Pricing</h4>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Specialized Root Cleaning - Basement Cleanout Access</span>
                        <span className="text-xl font-bold text-success-primary">$495</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                        <span className="font-semibold text-authority-navy">Specialized Root Cleaning - Toilet Access</span>
                        <span className="text-xl font-bold text-success-primary">$695</span>
                      </div>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Standard Service Includes</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Professional root cutting equipment
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Digital photos of notable findings
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Up to 75-100ft of mainline clearing
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Drainage testing post-service
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          New wax ring & bolts (toilet access)
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/services/sewer-services/milwaukee-sewer-flex-shaft-thornton.JPEG"
                        alt="Milwaukee sewer flex shaft equipment for professional root removal in Thornton Colorado"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Milwaukee Professional Equipment - Thornton
                        </h4>
                        <p className="text-sm text-trust-light">
                          Specialized flex shaft equipment for aggressive root removal
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Premium Root Removal with Video */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-success-light to-action-warm/10 rounded-2xl p-8 border-2 border-success-primary/20 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mr-6 shadow-lg">
                  <Camera size={32} className="text-neutral-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-authority-navy mb-2">Premium Root Removal with Video Recording</h3>
                  <p className="text-lg text-neutral-gray">Comprehensive root removal with full video documentation provided to customer</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-success-primary to-success-eco text-white p-4">
                      <h4 className="text-xl font-bold text-center">Premium Service Pricing</h4>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                      <span className="font-semibold text-authority-navy">Specialized Root Cleaning - Provide Sewer Video After</span>
                      <span className="text-xl font-bold text-success-primary">$795</span>
                    </div>
                    <div className="bg-success-light p-4">
                      <h5 className="font-bold text-success-primary mb-3">Premium Service Includes</h5>
                      <ul className="space-y-2 text-sm text-success-primary">
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Everything in standard service
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Full video camera inspection
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Before & after video recording
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Video provided to customer
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={16} className="mr-2" />
                          Complete pipe condition assessment
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-neutral-white rounded-xl shadow-lg border border-success-primary/10 overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src="/colorado-plumbing-uploads/services/sewer-services/root-specialized-sewer-cleaning-experts-plumber-littleton.png"
                        alt="Specialized sewer cleaning experts for root removal in Littleton Colorado"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h4 className="text-lg font-bold mb-1">
                          Specialized Root Cleaning Experts - Littleton
                        </h4>
                        <p className="text-sm text-trust-light">
                          Professional sewer cleaning specialists with advanced root removal capabilities
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA Section */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
              <h3 className="text-3xl font-bold mb-4">Ready for Professional Root Removal Service?</h3>
              <p className="text-xl mb-6 text-trust-light">
                See your exact price above • No hidden fees • Master plumber owned • Video documentation available
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="/hydro-jetting-pricing" 
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                >
                  💧 See Hydro-Jetting Pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section for Schema Parity */}
      <section className="py-16 bg-gradient-to-br from-neutral-white to-trust-light/30">
          <div className="container mx-auto px-4 max-w-4xl">
              <h2 className="text-3xl md:text-5xl font-bold mb-12 text-authority-navy text-center">
                  Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                  <div className="bg-white p-6 rounded-lg shadow-md border">
                      <h3 className="font-bold text-lg text-authority-navy">How much does sewer root removal cost in Denver?</h3>
                      <p className="text-neutral-gray mt-2">Colorado First Plumbing offers flat-rate pricing for root removal in the Denver area. Our standard service with photo proof is $495 via a cleanout or $695 via a pulled toilet. For an additional fee, our premium service at $795 includes a full video recording of the cleaning.</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md border">
                      <h3 className="font-bold text-lg text-authority-navy">What is the difference between standard and premium root removal?</h3>
                      <p className="text-neutral-gray mt-2">Our standard service uses professional root-cutting equipment and provides digital photos of any notable findings. The premium service includes everything in the standard package, plus a full before-and-after video camera inspection of the sewer line, which is provided to you for your records.</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md border">
                      <h3 className="font-bold text-lg text-authority-navy">Is root removal a permanent solution for tree roots in pipes?</h3>
                      <p className="text-neutral-gray mt-2">Root removal is a highly effective maintenance solution that clears the pipe and restores flow. However, if the pipe is damaged, roots will eventually grow back. Regular maintenance (every 1-2 years) is the most affordable way to manage them. For a permanent solution, a spot repair or full sewer line replacement may be necessary.</p>
                  </div>
              </div>
          </div>
      </section>

      <Footer />
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Sewer Root Removal Cost: $495 Flat-Rate | Colorado First Plumbing"
        description="See exact, flat-rate pricing for sewer root removal in Denver. Standard root cutting starts at $495. Premium service with video proof available. 360 Google Reviews."
        canonical="https://colorado-plumbing.com/root-removal-pricing"
        keywords="denver sewer root removal cost, tree root cutting price, sewer cleaning denver, remove roots from sewer line cost"
      />
    </div>
  );
};

export default RootRemovalPricing;